import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type AdminOverview } from '../../api';
import { Notice } from '../Panels';
import { Hint } from '../Hint';

/**
 * The admin landing page: headline counts for the workspace, this month's
 * usage, and a preview of the people and portals behind those numbers.
 */
export function Overview() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const reload = useCallback(() => {
    api
      .adminOverview()
      .then((overview) => {
        setData(overview);
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
      });
  }, []);

  useEffect(reload, [reload]);

  if (data === null) {
    return (
      <>
        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
        <p className="muted">
          <span className="spinner" /> Loading…
        </p>
      </>
    );
  }

  const { counts, limits, usage, workspace } = data;

  const bars: { label: string; used: number; allowed: number }[] = [
    { label: 'Datasets', used: usage.datasetsCreated, allowed: limits.datasetsPerMonth },
    { label: 'Rows', used: usage.rowsIngested, allowed: limits.rowsPerMonth },
    { label: 'AI summaries', used: usage.narrationsRun, allowed: limits.narrationsPerMonth },
  ];

  return (
    <>
      <header className="intro">
        <h2>Overview</h2>
        <p>
          {workspace.name} · the shape of the workspace at a glance.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      <div className="stat-grid">
        <StatCard label="People" value={counts.members} foot="in this workspace" />
        <StatCard label="Client portals" value={counts.portals} foot="branded client views" />
        <StatCard label="Datasets" value={counts.datasets} foot={`${counts.reports} reports`} />
        <StatCard label="Dashboards" value={counts.dashboards} foot="saved boards" />
        <StatCard label="Features" value="All" foot="every feature included" />
      </div>

      <section className="card">
        <header>
          <h2>
            This month's usage
            <Hint text="Fair-use counters for this month. Every feature is included; the caps only stop runaway scripts and restart on the 1st." />
          </h2>
          <span className="sub">resets on the 1st</span>
        </header>
        <div className="usage-bars">
          {bars.map((bar) => {
            const fraction = bar.allowed === 0 ? 0 : Math.min(1, bar.used / bar.allowed);
            return (
              <div key={bar.label}>
                <div className="usage-label">
                  <span>{bar.label}</span>
                  <span>
                    {bar.used.toLocaleString('en-US')} / {bar.allowed.toLocaleString('en-US')}
                  </span>
                </div>
                <div className="meter">
                  <div
                    style={{
                      width: `${Math.round(fraction * 100)}%`,
                      background:
                        fraction >= 1 ? 'var(--critical)' : fraction > 0.8 ? 'var(--warning)' : 'var(--series-1)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card">
        <header>
          <h2>People</h2>
          <span className="sub">
            {counts.members} {counts.members === 1 ? 'person' : 'people'} · manage them under People
          </span>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((member) => (
                <tr key={member.userId}>
                  <td>{member.name}</td>
                  <td>{member.email}</td>
                  <td>
                    <span className="badge">{member.role}</span>
                  </td>
                  <td>{new Date(member.joinedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <header>
          <h2>Client portals</h2>
          <span className="sub">
            {counts.portals} {counts.portals === 1 ? 'portal' : 'portals'} · manage them under Client portals
          </span>
        </header>
        {data.portals.length === 0 ? (
          <p className="muted">No client portals yet — create one to give a client a branded view of your reports.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="num">Reports</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.portals.map((portal) => (
                  <tr key={portal.id}>
                    <td>{portal.name}</td>
                    <td>
                      <span className={`badge ${portal.active ? 'good' : ''}`}>
                        {portal.active ? 'active' : 'paused'}
                      </span>
                    </td>
                    <td className="num">{portal.reportCount}</td>
                    <td>{new Date(portal.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function StatCard({ label, value, foot }: { label: string; value: number | string; foot: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{typeof value === 'number' ? value.toLocaleString('en-US') : value}</div>
      <div className="stat-foot">{foot}</div>
    </div>
  );
}
